import Swal from "sweetalert2";
import { linksPerKind, resolveBoardDims } from "./boardLayout";
import {
  getQuizByCategory,
  isQuizQuestionPlayable,
  normalizeLangKey,
} from "../pages/game/snakesLaddersUtils";
import { ensureThemeJsonArray } from "./themeAssets";
import { QUIZ_MIN_QUESTIONS } from "../admin/pages/themeupdate/quizHelpers";
import { isThemeFlagOn, readPlayerLanguage } from "../pages/gameSetupStorage";
import "./themeMissingSwal.css";

const LANG_LABELS = {
  english: "English",
  hindi: "Hindi",
  marathi: "Marathi",
};

const ALL_LANGS = ["english", "hindi", "marathi"];

function hasAsset(value) {
  return Boolean(String(value ?? "").trim());
}

function readRawQuizList(themeData) {
  const list = ensureThemeJsonArray(themeData?.quiz);
  if (list.length) return list;
  const questions = ensureThemeJsonArray(themeData?.questions);
  if (questions.length) return questions;
  return [];
}

/**
 * Playable questions for one language.
 * When `strict` is true, do not fall back to other languages' rows.
 */
function collectPlayableQuestions(themeData, lang, { strict = false } = {}) {
  const want = normalizeLangKey(lang);
  const nested = ensureThemeJsonArray(themeData?.[want]?.quiz);
  const raw = nested.length ? nested : readRawQuizList(themeData);
  const byLang = raw.filter(
    (q) => normalizeLangKey(q?.language || "english") === want
  );
  const source = strict ? byLang : byLang.length ? byLang : raw;
  return source.filter(isQuizQuestionPlayable);
}

function countSnakeLadder(themeData, lang, { strict = false } = {}) {
  if (!strict) {
    return getQuizByCategory(themeData, lang);
  }
  // Strict: only count rows tagged for this language (no cross-language fallback).
  const playable = collectPlayableQuestions(themeData, lang, { strict: true });
  const snakes = [];
  const ladders = [];
  playable.forEach((q) => {
    const cat = String(q?.category || "").trim().toLowerCase();
    if (cat === "snake" || cat === "snakes") snakes.push(q);
    else if (cat === "ladder" || cat === "ladders") ladders.push(q);
  });
  return { snakes, ladders };
}

function pushQuizGaps(missing, themeData, lang, perKind, { strict = false } = {}) {
  const label = LANG_LABELS[normalizeLangKey(lang)] || lang;
  const playable = collectPlayableQuestions(themeData, lang, { strict });
  if (playable.length < QUIZ_MIN_QUESTIONS) {
    missing.push(
      strict
        ? `${label}: at least ${QUIZ_MIN_QUESTIONS} playable questions (currently ${playable.length})`
        : `At least ${QUIZ_MIN_QUESTIONS} playable quiz questions (currently ${playable.length})`
    );
  }

  const { snakes, ladders } = countSnakeLadder(themeData, lang, { strict });
  if (snakes.length < perKind) {
    missing.push(
      strict
        ? `${label}: at least ${perKind} Snake questions (currently ${snakes.length})`
        : `At least ${perKind} Snake questions for this board size (currently ${snakes.length})`
    );
  }
  if (ladders.length < perKind) {
    missing.push(
      strict
        ? `${label}: at least ${perKind} Ladder questions (currently ${ladders.length})`
        : `At least ${perKind} Ladder questions for this board size (currently ${ladders.length})`
    );
  }
}

/**
 * Validate theme has everything needed to start Snakes & Ladders.
 * When MultiLanguage is on, English, Hindi and Marathi each need a full quiz set.
 * @returns {{ ok: boolean, missing: string[] }}
 */
export function validateThemeStageData(themeData, options = {}) {
  if (!themeData || typeof themeData !== "object") {
    return {
      ok: false,
      missing: [
        "Theme data is missing. Please configure the theme before starting the game.",
      ],
    };
  }

  const missing = [];

  if (!hasAsset(themeData.avtar1)) {
    missing.push("Female avatar image (Avatar 1)");
  }
  if (!hasAsset(themeData.avtar2)) {
    missing.push("Male avatar image (Avatar 2)");
  }

  const dims = resolveBoardDims(themeData);
  const perKind = linksPerKind(dims.cols * dims.rows);
  const multiLanguage = isThemeFlagOn(themeData?.MultiLanguage, false);

  if (multiLanguage) {
    ALL_LANGS.forEach((lang) => {
      pushQuizGaps(missing, themeData, lang, perKind, { strict: true });
    });
  } else {
    const lang = options.lang || readPlayerLanguage() || "english";
    pushQuizGaps(missing, themeData, lang, perKind, { strict: false });
  }

  return { ok: missing.length === 0, missing };
}

/** Shared Swal for incomplete theme stage data. */
export function fireThemeStageDataMissingSwal(missing = []) {
  const items = (missing || []).map((item) => String(item));
  const missingList = items
    .map((item) => `<li>${item.replace(/</g, "&lt;")}</li>`)
    .join("");
  const mentionsLang = items.some((item) =>
    /English|Hindi|Marathi/i.test(item)
  );

  return Swal.fire({
    icon: "warning",
    title: "Required game data missing",
    html: `
      <div class="sl-missing-card">
        <p class="sl-missing-lead">
          Add the missing items in
          <strong>Admin → Theme → Game Content</strong>
          ${
            mentionsLang
              ? "(avatars and questions for <strong>English, Hindi and Marathi</strong>)"
              : "(avatars and questions)"
          },
          then try again.
        </p>
        <ul class="sl-missing-list">${missingList}</ul>
      </div>
    `,
    confirmButtonText: "OK",
    allowOutsideClick: false,
    allowEscapeKey: false,
    buttonsStyling: false,
    customClass: {
      popup: "sl-missing-swal",
      confirmButton: "sl-missing-swal-btn",
    },
  });
}
